"use client";

/**
 * Interactive Featured Gear grid for industry landing pages.
 * Card body opens a homepage-style detail modal.
 * "Request Pricing →" is a sibling control (not nested) so touch targets pass a11y.
 */
import { useState } from "react";
import Image from "next/image";
import type { IndustryProduct } from "@/data/industry-verticals";
import { trackIndustryCtaClick } from "@/lib/analytics";
import { IndustryContactCta } from "./industry-contact-cta";
import { IndustryProductDetailModal } from "./industry-product-detail-modal";

type IndustryFeaturedProductsProps = {
  products: IndustryProduct[];
  // Industry slug for analytics (e.g. "highschool").
  vertical: string;
};

export function IndustryFeaturedProducts({
  products,
  vertical,
}: IndustryFeaturedProductsProps) {
  const [selectedProduct, setSelectedProduct] = useState<IndustryProduct | null>(
    null
  );

  const openProductModal = (product: IndustryProduct) => {
    // Track product card opens separately from quote CTAs.
    trackIndustryCtaClick({
      vertical,
      ctaLocation: "product_detail_open",
      label: product.name,
    });
    setSelectedProduct(product);
  };

  const closeProductModal = () => {
    setSelectedProduct(null);
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {products.map((product) => (
          // Card shell is not interactive. Detail + pricing are sibling controls so
          // Lighthouse touch targets do not nest (size/spacing audit).
          <article
            key={product.name}
            className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#28282B] text-left transition hover:border-[#8AD4E1]/50"
          >
            {/* Opens product detail modal - separate from Request Pricing below */}
            <button
              type="button"
              onClick={() => openProductModal(product)}
              className="flex flex-1 cursor-pointer flex-col text-left"
              aria-label={`View details for ${product.name}`}
            >
              {/* Product thumbnail */}
              <div className="relative h-52 w-full overflow-hidden bg-zinc-900">
                <Image
                  src={product.imageSrc}
                  // Descriptive alt text improves image SEO and accessibility.
                  alt={product.imageAlt}
                  fill
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  className="object-cover transition duration-300 group-hover:scale-105"
                />
              </div>

              {/* Product text */}
              <div className="flex flex-1 flex-col px-6 pt-6">
                <span className="text-xs font-semibold uppercase tracking-wider text-[#8AD4E1]">
                  {product.category}
                </span>
                <h3 className="mt-2 text-lg font-bold text-white">{product.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-400">
                  {product.description}
                </p>
              </div>
            </button>

            {/* Sibling CTA (not nested) with 48px min height for touch targets */}
            <div className="px-6 pb-6">
              <IndustryContactCta
                label="Request Pricing →"
                vertical={vertical}
                ctaLocation="product_card"
                variant="link"
              />
            </div>
          </article>
        ))}
      </div>

      {selectedProduct && (
        <IndustryProductDetailModal
          product={selectedProduct}
          vertical={vertical}
          onClose={closeProductModal}
        />
      )}
    </>
  );
}
